import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ApartmentOutlined,
  ClearOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import dayjs from "dayjs";

import { apiClient } from "../../services/api/apiClient";
import { usePageTitle } from "../../hooks";
import { PageHeader } from "../../components/common/PageHeader";

const { Text } = Typography;

interface Audit {
  id: number;
  reference: string;
  farm: number;
  farm_name: string;
  farm_surface: number | null;
  producer_name: string;
  province_name: string | null;
  town_name: string | null;
  season: number | null;
  season_name: string | null;
  audit_type_name: string | null;
  audit_state_name: string | null;
  auditor_user: number | null;
  auditor_name: string | null;
  planning_group: number | null;
  planning_group_name: string | null;
  planning_group_created: string | null;
  planning_date: string | null;
  start_date: string | null;
  country_portfolio: number | null;
  // Sampling plan — populated by the backend serializer.
  samples_to_take: number | null;
  samples_to_process: number | null;
  // Priority-1 contacts from the License / Farm chains. Null when no
  // active contact is on file for that audit's farm/producer.
  license_contact_name: string | null;
  license_contact_phone: string | null;
  license_contact_email: string | null;
  farm_contact_name: string | null;
  farm_contact_phone: string | null;
  farm_contact_email: string | null;
}

interface TeamMember {
  id: number;
  name: string;
  code: string | null;
  email: string;
  user_type: number;
  portfolio: number | null;
  allowed_portfolios: number[] | null;
}

type AssignedFilter = "all" | "unassigned" | "assigned";

/**
 * Resolve the *displayed* state of an audit. The DB-level
 * `audit_state` is always "Planificada" as soon as the row is
 * created — but the coordinator's mental model is more nuanced:
 *
 *   - no auditor                       → «Sin asignar»
 *   - auditor + no planning_date       → «Asignada»
 *   - auditor + planning_date          → «Planificada»
 *
 * Any state that already conveys an outcome (Cancelada, Finalizada,
 * En curso…) is shown as-is and never rewritten.
 */
const getDisplayState = (audit: Audit): string => {
  const raw = (audit.audit_state_name || "").trim();
  if (!raw) return "";
  const lowered = raw.toLowerCase();
  if (!lowered.includes("planific")) return raw;
  if (audit.auditor_user == null) return "Sin asignar";
  if (!audit.planning_date) return "Asignada";
  return raw;
};

/** Antd `Tag` colour for a given derived state string. */
const colorForDisplayState = (display: string): string => {
  const lowered = display.toLowerCase();
  if (display === "Sin asignar") return "gold";
  if (display === "Asignada") return "cyan";
  if (lowered.includes("cancel")) return "red";
  if (lowered.includes("finaliz")) return "green";
  if (lowered.includes("planific")) return "blue";
  return "default";
};

export const CoordinatorAssign = () => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();

  usePageTitle(t("coordinator.title", "Asignación de auditorías"));

  const [audits, setAudits] = useState<Audit[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtros sincronizados con la URL — al volver del detalle de una
  // auditoría con el back del navegador, los query params se conservan
  // y los controles se restauran solos.
  const [searchParams, setSearchParams] = useSearchParams();
  const updateParam = (key: string, value: string | number | undefined) => {
    setSearchParams(
      (prev) => {
        if (value === undefined || value === null || value === "")
          prev.delete(key);
        else prev.set(key, String(value));
        return prev;
      },
      { replace: true },
    );
  };
  const search = searchParams.get("q") ?? "";
  const setSearch = (value: string) => updateParam("q", value);
  // Quick assignment-state toggle — sits at the top of the toolbar so
  // the coordinator can flip between pending and done work without
  // opening the column-header filter on Auditor.
  const assignedFilter =
    (searchParams.get("assigned") as AssignedFilter | null) ?? "unassigned";
  const setAssignedFilter = (value: AssignedFilter) =>
    updateParam("assigned", value);
  // Planning is the mandatory primary filter — the table stays empty
  // until the coordinator picks one. This matches how they actually
  // work (one planning at a time, not a cross-planning mash-up).
  const planningIdFromUrl = searchParams.get("planning");
  const planningFilter: number | undefined =
    planningIdFromUrl != null && planningIdFromUrl !== ""
      ? Number(planningIdFromUrl)
      : undefined;
  const setPlanningFilter = (value: number | undefined) =>
    updateParam("planning", value);
  const [assigning, setAssigning] = useState<number | null>(null);
  // Every secondary filter (producer / province / town / type /
  // state / samples / auditor / contacts) lives in the column-header
  // dropdowns. The Table component owns that state internally; we
  // reset it by remounting the table whenever this counter — or the
  // planning — changes.
  const [tableResetCounter, setTableResetCounter] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Auditor-assignment modal: opened by the per-row icon button. We
  // keep the in-flight selection in local state so the user can cancel
  // without touching the underlying audit.
  const [assignTarget, setAssignTarget] = useState<Audit | null>(null);
  const [assignDraft, setAssignDraft] = useState<number | null>(null);

  const loadEverything = async () => {
    setLoading(true);
    try {
      const [auditsRes, teamRes] = await Promise.all([
        apiClient.get("/v1/coordinator/audits/", {
          params: { page_size: 1000, ordering: "-created" },
        }),
        apiClient.get("/v1/coordinator/team/", {
          params: { page_size: 200 },
        }),
      ]);
      setAudits(auditsRes.data?.data ?? auditsRes.data ?? []);
      setTeam(teamRes.data?.data ?? teamRes.data ?? []);
    } catch (e: any) {
      notify.error(t("coordinator.load_error", "No se pudo cargar la información del coordinador"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadEverything();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-audit options: only auditors whose portfolio matches the audit's
  // country (type-4 coordinators count via allowed_portfolios). When the
  // audit has no portfolio mapping (campaign country not in the COUNTRIES
  // tuple) we don't filter, so the coordinator can still dispatch.
  // We also always include the currently-assigned auditor — if the admin
  // already broke the rule the coordinator can still see/clear it.
  const buildTeamOptions = (audit: Audit) => {
    const audCountry = audit.country_portfolio;
    const eligible = (m: TeamMember) => {
      if (audCountry == null) return true;
      if (m.portfolio === audCountry) return true;
      if (m.user_type === 4 && (m.allowed_portfolios || []).includes(audCountry)) {
        return true;
      }
      return false;
    };
    const filtered = team.filter(
      (m) => eligible(m) || m.id === audit.auditor_user,
    );
    return filtered.map((m) => ({
      value: m.id,
      label:
        `${m.name}${m.code ? ` (${m.code})` : ""}` +
        `${m.user_type === 4 ? " · coord." : ""}` +
        (audCountry != null && !eligible(m) ? " · ⚠ otra cartera" : ""),
    }));
  };

  // Distinct plannings present in the loaded audit set. Drives the
  // mandatory planning picker — sorted by creation date descending
  // so the most recent planning sits at the top (matches how
  // coordinators think about their work).
  const planningOptions = useMemo(() => {
    const seen = new Map<number, { label: string; createdAt: number }>();
    for (const a of audits) {
      if (a.planning_group == null) continue;
      if (seen.has(a.planning_group)) continue;
      const createdAt = a.planning_group_created
        ? new Date(a.planning_group_created).getTime()
        : 0;
      seen.set(a.planning_group, {
        label: a.planning_group_name || `#${a.planning_group}`,
        createdAt,
      });
    }
    return Array.from(seen.entries())
      .map(([value, { label, createdAt }]) => ({ value, label, createdAt }))
      .sort((a, b) => {
        // Newest first; ties → alphabetical so the order is stable.
        if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
        return a.label.localeCompare(b.label);
      })
      .map(({ value, label }) => ({ value, label }));
  }, [audits]);

  // Rows scoped to the selected planning. Drives both the column
  // filter options below and the table dataSource (further refined
  // by the top-bar search).
  const scopedAudits = useMemo(() => {
    if (planningFilter === undefined) return [];
    return audits.filter((a) => a.planning_group === planningFilter);
  }, [audits, planningFilter]);

  // Filter options for the per-column dropdowns. Antd expects
  // `{text, value}` entries. We derive them from `scopedAudits` so
  // dropdowns only show values present in the currently-selected
  // planning, not every value in the company.
  const buildColumnFilters = (key: keyof Audit) =>
    Array.from(
      new Set(
        scopedAudits
          .map((a) => a[key])
          .filter((v) => v != null && v !== "")
          .map((v) => String(v)),
      ),
    )
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ text: v, value: v }));

  const producerFilters = useMemo(
    () => buildColumnFilters("producer_name"),
    [scopedAudits],
  );
  const farmFilters = useMemo(
    () => buildColumnFilters("farm_name"),
    [scopedAudits],
  );
  const licenseContactNameFilters = useMemo(
    () => buildColumnFilters("license_contact_name"),
    [scopedAudits],
  );
  const licenseContactPhoneFilters = useMemo(
    () => buildColumnFilters("license_contact_phone"),
    [scopedAudits],
  );
  const licenseContactEmailFilters = useMemo(
    () => buildColumnFilters("license_contact_email"),
    [scopedAudits],
  );
  const farmContactNameFilters = useMemo(
    () => buildColumnFilters("farm_contact_name"),
    [scopedAudits],
  );
  const farmContactPhoneFilters = useMemo(
    () => buildColumnFilters("farm_contact_phone"),
    [scopedAudits],
  );
  const farmContactEmailFilters = useMemo(
    () => buildColumnFilters("farm_contact_email"),
    [scopedAudits],
  );
  const provinceFilters = useMemo(
    () => buildColumnFilters("province_name"),
    [scopedAudits],
  );
  const townFilters = useMemo(
    () => buildColumnFilters("town_name"),
    [scopedAudits],
  );
  const auditTypeFilters = useMemo(
    () => buildColumnFilters("audit_type_name"),
    [scopedAudits],
  );
  // State filters use the same derived display label as the cell —
  // see `getDisplayState` for the full ruleset.
  const stateFilters = useMemo(() => {
    const seen = new Set<string>();
    for (const a of scopedAudits) {
      const display = getDisplayState(a);
      if (display) seen.add(display);
    }
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ text: v, value: v }));
  }, [scopedAudits]);
  // Auditor filter: every assigned auditor in the planning plus a
  // synthetic "__unassigned__" option for null rows.
  const auditorFilters = useMemo(() => {
    const seen = new Map<string, string>();
    let hasUnassigned = false;
    for (const a of scopedAudits) {
      if (a.auditor_user == null) {
        hasUnassigned = true;
        continue;
      }
      const key = String(a.auditor_user);
      if (!seen.has(key)) {
        seen.set(key, a.auditor_name || `#${a.auditor_user}`);
      }
    }
    const entries = Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ text: label, value }));
    if (hasUnassigned) {
      entries.unshift({
        text: t("coordinator.no_auditor", "Sin asignar"),
        value: "__unassigned__",
      });
    }
    return entries;
  }, [scopedAudits, t]);

  // Rows fed to the Table: scoped to the selected planning, the
  // assignment-state toggle and the top-bar search. The rest of the
  // filters live in the column headers and are managed by antd.
  const displayedAudits = useMemo(() => {
    if (planningFilter === undefined) return [];
    let rows = audits.filter((a) => a.planning_group === planningFilter);

    if (assignedFilter === "unassigned") {
      rows = rows.filter((a) => a.auditor_user == null);
    } else if (assignedFilter === "assigned") {
      rows = rows.filter((a) => a.auditor_user != null);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((a) =>
        [
          a.reference,
          a.farm_name,
          a.producer_name,
          a.province_name,
          a.town_name,
          a.season_name,
          a.auditor_name,
          // Contact data — let the global search hit any of name /
          // phone / email for either chain so the coordinator can
          // paste a number or e-mail and pinpoint the row.
          a.license_contact_name,
          a.license_contact_phone,
          a.license_contact_email,
          a.farm_contact_name,
          a.farm_contact_phone,
          a.farm_contact_email,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
    }
    return rows;
  }, [audits, planningFilter, search, assignedFilter]);

  // Counts that drive the Segmented labels — based on the planning
  // scope, ignoring search / column filters so the user always sees
  // the totals for the planning.
  const unassignedCount = scopedAudits.filter((a) => a.auditor_user == null).length;
  const assignedCount = scopedAudits.length - unassignedCount;

  const assign = async (auditId: number, auditorId: number | null) => {
    setAssigning(auditId);
    try {
      const res = await apiClient.patch<Audit>(
        `/v1/coordinator/audits/${auditId}/assign/`,
        { auditor_user: auditorId },
      );
      // Update the row in place.
      setAudits((prev) => prev.map((a) => (a.id === auditId ? res.data : a)));
      notify.success(
        auditorId
          ? t("coordinator.assign_success", "Auditor asignado")
          : t("coordinator.unassign_success", "Auditor retirado"),
      );
    } catch (e: any) {
      const detail = e?.originalError?.response?.data?.detail
        || e?.response?.data?.detail
        || e?.message;
      notify.error(detail || t("coordinator.assign_error", "No se pudo asignar"));
    } finally {
      setAssigning(null);
    }
  };

  // Download the canonical, unfiltered audit table as .xlsx. Hits the
  // coordinator-scoped backend endpoint which already restricts to the
  // user's audit company.
  const handleExport = async () => {
    if (planningFilter === undefined) {
      notify.warning(
        t(
          "coordinator.export_requires_planning",
          "Selecciona primero una planificación para exportar.",
        ),
      );
      return;
    }
    setExporting(true);
    try {
      const response = await apiClient.get<Blob>(
        "/v1/coordinator/audits/export/",
        {
          params: { planning_group: planningFilter },
          responseType: "blob",
        },
      );
      const cd = (response.headers as any)["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(cd);
      const filename =
        match?.[1] || "asignacion-coordinador.xlsx";

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      notify.error(
        e?.response?.data?.detail ||
          e?.message ||
          t("coordinator.export_error", "No se pudo exportar el Excel."),
      );
    } finally {
      setExporting(false);
    }
  };

  // Open the assignment modal for `audit` and seed the draft with
  // whoever is currently assigned so the user can either swap or
  // clear.
  const openAssignModal = (audit: Audit) => {
    setAssignTarget(audit);
    setAssignDraft(audit.auditor_user ?? null);
  };

  const closeAssignModal = () => {
    setAssignTarget(null);
    setAssignDraft(null);
  };

  const confirmAssignModal = async () => {
    if (!assignTarget) return;
    await assign(assignTarget.id, assignDraft ?? null);
    closeAssignModal();
  };

  // Reset the search box AND every per-column filter (by bumping the
  // Table's remount key). Keeps the planning selection — that's the
  // primary scope, not a filter the coordinator wants to drop with a
  // single click.
  const clearAllFilters = () => {
    setSearch("");
    setAssignedFilter("all");
    setTableResetCounter((n) => n + 1);
  };

  return (
    <Card>
      <PageHeader
        icon={<ApartmentOutlined />}
        title={t("coordinator.title", "Asignación de auditorías")}
        subtitle={t(
          "coordinator.subtitle",
          "Reparte las auditorías pendientes de tu empresa entre los auditores de tu equipo.",
        )}
        extra={
          <Space>
            <Tooltip
              title={
                planningFilter === undefined
                  ? t(
                      "coordinator.export_requires_planning",
                      "Selecciona primero una planificación para exportar.",
                    )
                  : t(
                      "coordinator.export_tooltip",
                      "Exporta las auditorías de la planificación seleccionada a Excel.",
                    )
              }
            >
              <Button
                icon={<DownloadOutlined />}
                loading={exporting}
                onClick={handleExport}
                disabled={planningFilter === undefined}
              >
                {t("coordinator.export", "Exportar Excel")}
              </Button>
            </Tooltip>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadEverything}
              loading={loading}
            >
              {t("common.refresh", "Actualizar")}
            </Button>
          </Space>
        }
      />

      {/* Top toolbar: planning picker (mandatory) + free-text search.
          Every other filter lives in the column headers — clicking
          the funnel icon next to a column title brings up the
          picker. */}
      <Space size="middle" style={{ marginBottom: 12 }} wrap>
        <Select<number>
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t(
            "coordinator.pick_planning",
            "Selecciona una planificación…",
          )}
          style={{ minWidth: 360 }}
          value={planningFilter}
          onChange={(v) => setPlanningFilter(v)}
          options={planningOptions}
          disabled={planningOptions.length === 0}
        />
        {planningFilter !== undefined && (
          <Segmented<AssignedFilter>
            value={assignedFilter}
            onChange={(v) => setAssignedFilter(v as AssignedFilter)}
            options={[
              {
                label: `${t("coordinator.filter_unassigned", "Sin asignar")} (${unassignedCount})`,
                value: "unassigned",
              },
              {
                label: `${t("coordinator.filter_assigned", "Asignadas")} (${assignedCount})`,
                value: "assigned",
              },
              {
                label: `${t("coordinator.filter_all", "Todas")} (${scopedAudits.length})`,
                value: "all",
              },
            ]}
          />
        )}
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t(
            "coordinator.search_placeholder",
            "Buscar por referencia, finca, productor, contacto…",
          )}
          style={{ width: 360 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={planningFilter === undefined}
        />
        <Tooltip
          title={t(
            "coordinator.clear_filters_tooltip",
            "Borra la búsqueda y todos los filtros de columna. La planificación se mantiene.",
          )}
        >
          <Button
            icon={<ClearOutlined />}
            onClick={clearAllFilters}
            disabled={planningFilter === undefined}
          >
            {t("coordinator.clear_filters", "Limpiar filtros")}
          </Button>
        </Tooltip>
        <Tag icon={<TeamOutlined />} color="purple">
          {t("coordinator.team_size", "{{n}} miembros en tu equipo", { n: team.length })}
        </Tag>
      </Space>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
      ) : audits.length === 0 ? (
        <Empty
          description={t(
            "coordinator.empty_company",
            "No hay auditorías asignadas a tu empresa todavía.",
          )}
        />
      ) : planningFilter === undefined ? (
        <Alert
          showIcon
          type="info"
          message={t(
            "coordinator.pick_planning_title",
            "Selecciona una planificación para empezar",
          )}
          description={t(
            "coordinator.pick_planning_help",
            "Esta vista trabaja una planificación cada vez. Elige una en el desplegable de arriba para ver las auditorías, filtrarlas y asignar auditores.",
          )}
        />
      ) : (
        <Table<Audit>
          // Remount the table whenever the planning changes — or the
          // user hits "Limpiar filtros" — so antd's internally-owned
          // column-filter state resets to "no filter applied".
          key={`${planningFilter}-${tableResetCounter}`}
          dataSource={displayedAudits}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ["25", "50", "100", "200"] }}
          scroll={{ x: 2430 }}
          // Compact look — coordinator asked for "more info per page".
          // We drop font + padding via a className handled in a tiny
          // local <style> block below so we don't have to wrap every
          // cell render() in inline styles.
          className="coord-assign-table"
          locale={{ emptyText: t("coordinator.empty_filtered", "Ningún resultado con los filtros actuales") }}
        >
          <Table.Column<Audit>
            title={t("audit.producer", "Productor")}
            dataIndex="producer_name"
            width={180}
            sorter={(a, b) => (a.producer_name || "").localeCompare(b.producer_name || "")}
            // Default sort: A→Z by producer. The Table remounts via
            // `key` whenever the planning changes or the user hits
            // "Limpiar filtros", which restores this default for free.
            defaultSortOrder="ascend"
            filters={producerFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.producer_name || "") === String(value)
            }
            render={(v: string | null) => v || "—"}
          />
          <Table.Column<Audit>
            title={t("audit.farm", "Finca")}
            dataIndex="farm_name"
            width={160}
            sorter={(a, b) => (a.farm_name || "").localeCompare(b.farm_name || "")}
            filters={farmFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.farm_name || "") === String(value)
            }
            render={(v: string | null) => v || "—"}
          />
          <Table.Column<Audit>
            title={t("audit.province", "Provincia")}
            dataIndex="province_name"
            width={140}
            sorter={(a, b) => (a.province_name || "").localeCompare(b.province_name || "")}
            filters={provinceFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.province_name || "") === String(value)
            }
            render={(v: string | null) => v || "—"}
          />
          <Table.Column<Audit>
            title={t("audit.town", "Municipio")}
            dataIndex="town_name"
            width={140}
            sorter={(a, b) => (a.town_name || "").localeCompare(b.town_name || "")}
            filters={townFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.town_name || "") === String(value)
            }
            render={(v: string | null) => v || "—"}
          />
          <Table.Column<Audit>
            title={t("audit.surface", "Superficie")}
            dataIndex="farm_surface"
            width={120}
            align="right"
            sorter={(a, b) => (a.farm_surface ?? 0) - (b.farm_surface ?? 0)}
            render={(v: number | null) =>
              v != null ? `${v.toFixed(2)} ha` : "—"}
          />
          <Table.Column<Audit>
            title={t("planning.audit_type", "Tipo")}
            dataIndex="audit_type_name"
            width={120}
            sorter={(a, b) => (a.audit_type_name || "").localeCompare(b.audit_type_name || "")}
            filters={auditTypeFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.audit_type_name || "") === String(value)
            }
            render={(v: string | null) => v || "—"}
          />
          <Table.Column<Audit>
            title={t("audit.reference", "Referencia")}
            dataIndex="reference"
            width={130}
            sorter={(a, b) => (a.reference || "").localeCompare(b.reference || "")}
          />
          <Table.Column<Audit>
            title={t("audit.state", "Estado")}
            key="audit_state_name"
            width={140}
            // Sort on the derived display state so "Sin asignar" /
            // "Asignada" / "Planificada" rows cluster correctly
            // instead of mixing under the raw catalogue value.
            sorter={(a, b) =>
              getDisplayState(a).localeCompare(getDisplayState(b))
            }
            filters={stateFilters}
            filterSearch
            onFilter={(value, record) =>
              getDisplayState(record) === String(value)
            }
            render={(_: unknown, audit: Audit) => {
              const display = getDisplayState(audit);
              if (!display) {
                return <span style={{ color: "#bfbfbf" }}>—</span>;
              }
              const tag = (
                <Tag color={colorForDisplayState(display)} style={{ margin: 0 }}>
                  {display}
                </Tag>
              );
              // For the derived states (Sin asignar / Asignada) the
              // DB-level value still says "Planificada" — show that
              // in a tooltip so the semantics aren't lost.
              const rawState = audit.audit_state_name || "";
              if (display !== rawState && rawState) {
                return (
                  <Tooltip
                    title={t(
                      "coordinator.derived_state_tooltip",
                      "Estado oficial: {{state}}. Mostrada como «{{display}}» según auditor y fecha de planificación.",
                      { state: rawState, display },
                    )}
                  >
                    {tag}
                  </Tooltip>
                );
              }
              return tag;
            }}
          />
          <Table.Column<Audit>
            title={t("audit.planning_date", "Fecha planificada")}
            dataIndex="planning_date"
            width={130}
            sorter={(a, b) => {
              if (!a.planning_date && !b.planning_date) return 0;
              if (!a.planning_date) return 1;
              if (!b.planning_date) return -1;
              return (
                new Date(a.planning_date).getTime() -
                new Date(b.planning_date).getTime()
              );
            }}
            render={(v: string | null) =>
              v ? (
                dayjs(v).format("DD/MM/YYYY")
              ) : (
                <span style={{ color: "#bfbfbf" }}>—</span>
              )
            }
          />
          <Table.Column<Audit>
            title={t("audit.samples", "Muestras")}
            dataIndex="samples_to_take"
            width={110}
            align="center"
            sorter={(a, b) =>
              (a.samples_to_take || 0) - (b.samples_to_take || 0)
            }
            filters={[
              { text: t("audit.samples_filter_yes", "Con muestras"), value: "yes" },
              { text: t("audit.samples_filter_no", "Sin muestras"), value: "no" },
            ]}
            filterMultiple={false}
            onFilter={(value, record) => {
              const needs = (record.samples_to_take || 0) > 0;
              return value === "yes" ? needs : !needs;
            }}
            render={(value: number | null) => {
              const n = value || 0;
              if (n <= 0) {
                return <Tag color="default">{t("common.no", "No")}</Tag>;
              }
              return (
                <Tag color="blue">
                  {t("audit.samples_count", "{{count}} a tomar", { count: n })}
                </Tag>
              );
            }}
          />
          {/* Contact info split into independent sortable / filterable
              columns so the coordinator can sort by phone or copy an
              email straight out of one cell. */}
          <Table.Column<Audit>
            title={t("audit.license_contact_name", "Contacto Licencia")}
            dataIndex="license_contact_name"
            width={180}
            sorter={(a, b) =>
              (a.license_contact_name || "").localeCompare(b.license_contact_name || "")
            }
            filters={licenseContactNameFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.license_contact_name || "") === String(value)
            }
            render={(v: string | null) => v || "—"}
          />
          <Table.Column<Audit>
            title={t("audit.license_contact_phone", "Tel. Licencia")}
            dataIndex="license_contact_phone"
            width={150}
            sorter={(a, b) =>
              (a.license_contact_phone || "").localeCompare(b.license_contact_phone || "")
            }
            filters={licenseContactPhoneFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.license_contact_phone || "") === String(value)
            }
            render={(v: string | null) =>
              v ? (
                <a
                  href={`tel:${v}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: "#1677ff" }}
                >
                  {v}
                </a>
              ) : (
                "—"
              )
            }
          />
          <Table.Column<Audit>
            title={t("audit.license_contact_email", "Email Licencia")}
            dataIndex="license_contact_email"
            width={220}
            sorter={(a, b) =>
              (a.license_contact_email || "").localeCompare(b.license_contact_email || "")
            }
            filters={licenseContactEmailFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.license_contact_email || "") === String(value)
            }
            render={(v: string | null) =>
              v ? (
                <a
                  href={`mailto:${v}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: "#1677ff" }}
                  title={v}
                >
                  {v}
                </a>
              ) : (
                "—"
              )
            }
          />
          <Table.Column<Audit>
            title={t("audit.farm_contact_name", "Contacto Finca")}
            dataIndex="farm_contact_name"
            width={180}
            sorter={(a, b) =>
              (a.farm_contact_name || "").localeCompare(b.farm_contact_name || "")
            }
            filters={farmContactNameFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.farm_contact_name || "") === String(value)
            }
            render={(v: string | null) => v || "—"}
          />
          <Table.Column<Audit>
            title={t("audit.farm_contact_phone", "Tel. Finca")}
            dataIndex="farm_contact_phone"
            width={150}
            sorter={(a, b) =>
              (a.farm_contact_phone || "").localeCompare(b.farm_contact_phone || "")
            }
            filters={farmContactPhoneFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.farm_contact_phone || "") === String(value)
            }
            render={(v: string | null) =>
              v ? (
                <a
                  href={`tel:${v}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: "#1677ff" }}
                >
                  {v}
                </a>
              ) : (
                "—"
              )
            }
          />
          <Table.Column<Audit>
            title={t("audit.farm_contact_email", "Email Finca")}
            dataIndex="farm_contact_email"
            width={220}
            sorter={(a, b) =>
              (a.farm_contact_email || "").localeCompare(b.farm_contact_email || "")
            }
            filters={farmContactEmailFilters}
            filterSearch
            onFilter={(value, record) =>
              (record.farm_contact_email || "") === String(value)
            }
            render={(v: string | null) =>
              v ? (
                <a
                  href={`mailto:${v}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: "#1677ff" }}
                  title={v}
                >
                  {v}
                </a>
              ) : (
                "—"
              )
            }
          />
          <Table.Column<Audit>
            title={t("audit.auditor", "Auditor")}
            key="auditor"
            width={220}
            fixed="right"
            filters={auditorFilters}
            filterSearch
            onFilter={(value, record) => {
              if (value === "__unassigned__") return record.auditor_user == null;
              return String(record.auditor_user ?? "") === String(value);
            }}
            render={(_: unknown, audit: Audit) => {
              const hasAuditor = audit.auditor_user != null;
              return (
                <Space size={4} align="center">
                  {hasAuditor ? (
                    <Tag color="blue" style={{ margin: 0 }}>
                      {audit.auditor_name || `#${audit.auditor_user}`}
                    </Tag>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("coordinator.no_auditor", "Sin asignar")}
                    </Text>
                  )}
                  <Tooltip
                    title={
                      hasAuditor
                        ? t("coordinator.reassign", "Reasignar auditor")
                        : t("coordinator.pick_auditor", "Asignar auditor")
                    }
                  >
                    <Button
                      size="small"
                      type={hasAuditor ? "default" : "primary"}
                      icon={
                        hasAuditor ? <UserSwitchOutlined /> : <UserAddOutlined />
                      }
                      onClick={() => openAssignModal(audit)}
                      loading={assigning === audit.id}
                      aria-label={
                        hasAuditor
                          ? t("coordinator.reassign", "Reasignar auditor")
                          : t("coordinator.pick_auditor", "Asignar auditor")
                      }
                    />
                  </Tooltip>
                  {hasAuditor && (
                    <Tooltip title={t("coordinator.unassign", "Retirar asignación")}>
                      <Button
                        size="small"
                        type="text"
                        icon={<ClearOutlined />}
                        onClick={() => assign(audit.id, null)}
                        loading={assigning === audit.id}
                        aria-label={t("coordinator.unassign", "Retirar asignación")}
                      />
                    </Tooltip>
                  )}
                </Space>
              );
            }}
          />
        </Table>
      )}

      <div style={{ marginTop: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(
            "coordinator.help",
            "Solo aparecen aquí las auditorías cuyas planificaciones la administración asignó a tu empresa. " +
            "El auditor que selecciones debe pertenecer a tu equipo.",
          )}
        </Text>
      </div>

      <Modal
        open={assignTarget != null}
        onCancel={closeAssignModal}
        onOk={confirmAssignModal}
        confirmLoading={assigning === assignTarget?.id}
        title={
          assignTarget?.auditor_user != null
            ? t("coordinator.reassign_auditor", "Reasignar auditor")
            : t("coordinator.assign_auditor", "Asignar auditor")
        }
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnClose
        width={520}
      >
        {assignTarget && (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("audit.producer", "Productor")} ·{" "}
                {t("audit.farm", "Finca")} ·{" "}
                {t("audit.reference", "Referencia")}
              </Text>
              <div style={{ fontWeight: 500 }}>
                {assignTarget.producer_name || "—"} ·{" "}
                {assignTarget.farm_name || "—"} ·{" "}
                <span style={{ color: "#8c8c8c" }}>
                  {assignTarget.reference || "—"}
                </span>
              </div>
            </div>
            <Select<number>
              autoFocus
              showSearch
              allowClear
              placeholder={t(
                "coordinator.pick_auditor",
                "Selecciona auditor…",
              )}
              style={{ width: "100%" }}
              optionFilterProp="label"
              options={buildTeamOptions(assignTarget)}
              value={assignDraft ?? undefined}
              onChange={(v) => setAssignDraft(v ?? null)}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t(
                "coordinator.assign_modal_help",
                "Solo se listan auditores de tu empresa cuya cartera coincide con el país de la auditoría.",
              )}
            </Text>
          </Space>
        )}
      </Modal>

      <style>{`
        /* Density tweaks for the dispatch table — keep things scoped
           via the wrapping className so we don't bleed into the rest
           of the app's antd tables. */
        .coord-assign-table .ant-table-thead > tr > th,
        .coord-assign-table .ant-table-tbody > tr > td {
          padding: 4px 8px !important;
          font-size: 12px;
          line-height: 1.35;
        }
        .coord-assign-table .ant-table-thead > tr > th {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2px;
          background: #fafafa;
        }
        .coord-assign-table .ant-tag {
          font-size: 11px;
          line-height: 16px;
          padding: 0 6px;
          margin: 0;
        }
        .coord-assign-table .ant-btn-sm {
          height: 22px;
          padding: 0 6px;
          font-size: 11px;
        }
      `}</style>
    </Card>
  );
};

export default CoordinatorAssign;
